import { apiClient } from '@/lib/api/client'
import type {
  SupportTicket,
  SupportMessage,
  SupportAgentAction,
  SupportContact,
  SupportCannedResponse,
  SupportAgentConfig,
  SupportStats,
  TicketStatus,
  TicketPriority,
  TicketIntent,
} from '@/types/entities'

// ─── Request / Response Types ─────────────────────────────

export interface TicketListParams {
  product?: 'memoryframe' | 'marketplace' | 'unknown'
  status?: TicketStatus
  priority?: TicketPriority
  intent?: TicketIntent
  assignedTo?: number
  limit?: number
  offset?: number
}

export interface TicketListResponse {
  data: SupportTicket[]
  count: number
}

export interface ContactListParams {
  search?: string
  limit?: number
  offset?: number
}

export interface ContactListResponse {
  data: SupportContact[]
  count: number
}

export interface TicketUpdatePayload {
  status?: TicketStatus
  priority?: TicketPriority
  assignedTo?: number | null
  tags?: string[]
  internalNote?: string
}

export interface SendReplyPayload {
  text: string
  html?: string
}

/** A refund the customer asked for, as artwork-ms found it (lib/support/decisions.js). */
export interface RefundDecision {
  type: 'refund'
  summary: string
  canApprove: boolean
  /** What Approve does: see refundPlan in decisions.js. */
  plan: 'cancel_and_refund' | 'printing' | 'shipped' | 'already_refunded' | 'manual' | 'no_order'
  order: {
    name: string
    total: string
    currency: string
    placedAt: string
    financialStatus: string
    fulfillmentStatus: string
    items: string[]
  } | null
  print: { id: string; stage: string; printing: boolean; shipping: boolean } | { error: string } | null
  /** 'ok', 'missing_write_orders', or 'unknown: <why>' (Shopify permission to refund). */
  refundAccess?: string
}

export interface DuplicateAccount {
  id: number
  login: string
  name: string | null
  handle: string | null
  created: string
  lastLogin: string | null
  live: number
  artworks: number
  wroteFrom: boolean
}

export interface DuplicateAccountDecision {
  type: 'duplicate_account'
  summary: string
  canApprove: boolean
  accounts: DuplicateAccount[]
  requestedName: string | null
  suggestedKeepUserId: number
}

export interface HeldClosureDecision {
  type: 'held_closure'
  summary: string
  canApprove: boolean
  requestId: string
  confirmedAt: string
  account: { id: number; login: string; name: string | null; handle: string | null; live: number; artworks: number } | null
  /** Every account closed in the last 24 hours, which is why this one was held. */
  closuresInLastDay: { userId: number; name: string | null; at: string; how: string }[]
}

export interface DecidedDecision {
  type: string
  decided: { outcome: 'approved' | 'declined' | 'in_progress'; by: string | null; at: string; summary?: string; notifiedAt?: string }
}

export type TicketDecision =
  | RefundDecision
  | DuplicateAccountDecision
  | HeldClosureDecision
  | DecidedDecision
  | { type: 'none' }

export interface DecidePayload {
  outcome: 'approved' | 'declined'
  keepUserId?: number
  refundEvenIfPrinting?: boolean
  note?: string
}

export interface AgentPromptResult {
  id: string
  ticketId: string
  parentTaskId: string | null
  prompt: string
  status: 'running' | 'waiting' | 'completed' | 'needs_attention'
  response: string | null
  errorMessage: string | null
  createdAt: string
  completedAt: string | null
  outcomes: Array<{
    id: number; name: string; success: boolean; taskId?: string; error?: string | null
    publishingStatus?: 'waiting' | 'completed' | 'needs_attention'
  }>
}

// ─── API Client ─────────────────────────────────────────

export const supportApi = {
  getAgentTasks: async (ticketId: string): Promise<AgentPromptResult[]> => {
    const response = await apiClient.get<AgentPromptResult[]>(`/api/support/tickets/${ticketId}/agent-tasks`)
    return response.data
  },
  promptAgent: async (ticketId: string, prompt: string, parentTaskId?: string | null): Promise<AgentPromptResult> => {
    const response = await apiClient.post<AgentPromptResult>(
      `/api/support/tickets/${ticketId}/agent-prompt`, { prompt, parentTaskId }, { timeout: 180000 }
    )
    return response.data
  },

  // ─── Dashboard ──────────────────────────────────────

  getStats: async (): Promise<SupportStats> => {
    const response = await apiClient.get<SupportStats>('/api/support/stats')
    return response.data
  },

  // ─── Tickets ────────────────────────────────────────

  getTickets: async (params: TicketListParams = {}): Promise<TicketListResponse> => {
    const response = await apiClient.get('/api/support/tickets', {
      params: {
        status: params.status,
        product: params.product,
        priority: params.priority,
        intent: params.intent,
        assignedTo: params.assignedTo,
        limit: params.limit || 25,
        offset: params.offset || 0,
      },
    })
    // The count comes from a response header
    const count = parseInt(response.headers['count'] || '0', 10)
    return { data: response.data, count }
  },

  getTicket: async (ticketId: string): Promise<SupportTicket> => {
    const response = await apiClient.get<SupportTicket>(`/api/support/tickets/${ticketId}`)
    return response.data
  },

  updateTicket: async (ticketId: string, data: TicketUpdatePayload): Promise<SupportTicket> => {
    const response = await apiClient.patch<SupportTicket>(`/api/support/tickets/${ticketId}`, data)
    return response.data
  },

  updateTicketStatus: async (ticketId: string, status: TicketStatus): Promise<SupportTicket> => {
    const response = await apiClient.patch<SupportTicket>(
      `/api/support/tickets/${ticketId}/status`,
      { status }
    )
    return response.data
  },

  // ─── Messages ───────────────────────────────────────

  getMessages: async (ticketId: string): Promise<SupportMessage[]> => {
    const response = await apiClient.get<SupportMessage[]>(
      `/api/support/tickets/${ticketId}/messages`
    )
    return response.data
  },

  sendReply: async (ticketId: string, payload: SendReplyPayload): Promise<unknown> => {
    const response = await apiClient.post(`/api/support/tickets/${ticketId}/reply`, payload)
    return response.data
  },

  // ─── Decisions ──────────────────────────────────────

  getDecision: async (ticketId: string): Promise<TicketDecision> => {
    const response = await apiClient.get<TicketDecision>(`/api/support/tickets/${ticketId}/decision`)
    return response.data
  },

  decide: async (ticketId: string, payload: DecidePayload): Promise<unknown> => {
    // Cancelling an order waits on Shopify, and drafting the notification on the agent.
    const response = await apiClient.post(`/api/support/tickets/${ticketId}/decision`, payload, { timeout: 120000 })
    return response.data
  },

  // ─── Agent Actions ──────────────────────────────────

  getAgentActions: async (ticketId: string): Promise<SupportAgentAction[]> => {
    const response = await apiClient.get<SupportAgentAction[]>(
      `/api/support/tickets/${ticketId}/actions`
    )
    return response.data
  },

  // ─── Contacts ───────────────────────────────────────

  getContacts: async (params: ContactListParams = {}): Promise<ContactListResponse> => {
    const response = await apiClient.get('/api/support/contacts', {
      params: {
        search: params.search,
        limit: params.limit || 25,
        offset: params.offset || 0,
      },
    })
    const count = parseInt(response.headers['count'] || '0', 10)
    return { data: response.data, count }
  },

  getContact: async (contactId: string): Promise<SupportContact> => {
    const response = await apiClient.get<SupportContact>(`/api/support/contacts/${contactId}`)
    return response.data
  },

  // ─── Canned Responses ──────────────────────────────

  getCannedResponses: async (): Promise<SupportCannedResponse[]> => {
    const response = await apiClient.get<SupportCannedResponse[]>(
      '/api/support/canned-responses'
    )
    return response.data
  },

  createCannedResponse: async (
    data: Pick<SupportCannedResponse, 'title' | 'body' | 'category'> & { variables?: string[] }
  ): Promise<SupportCannedResponse> => {
    const response = await apiClient.post<SupportCannedResponse>(
      '/api/support/canned-responses',
      data
    )
    return response.data
  },

  updateCannedResponse: async (
    id: string,
    data: Partial<Pick<SupportCannedResponse, 'title' | 'body' | 'category' | 'isActive'>>
  ): Promise<SupportCannedResponse> => {
    const response = await apiClient.put<SupportCannedResponse>(
      `/api/support/canned-responses/${id}`,
      data
    )
    return response.data
  },

  deleteCannedResponse: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/support/canned-responses/${id}`)
  },

  // ─── Agent Config ──────────────────────────────────

  getAgentConfig: async (): Promise<SupportAgentConfig[]> => {
    const response = await apiClient.get<SupportAgentConfig[]>('/api/support/agent-config')
    return response.data
  },

  updateAgentConfig: async (key: string, value: unknown): Promise<SupportAgentConfig> => {
    const response = await apiClient.put<SupportAgentConfig>(
      `/api/support/agent-config/${key}`,
      { value }
    )
    return response.data
  },
}
